
import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AuthFormContainer from "@/components/auth/AuthFormContainer";
import SignInForm from "@/components/auth/SignInForm";
import VerificationErrorDialog from "@/components/auth/VerificationErrorDialog";
import EmailVerification from "@/components/auth/EmailVerification";
import { useAuth } from "@/hooks/useAuth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const AuthPage: React.FC = () => {
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const location = useLocation();
  const [redirecting, setRedirecting] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  
  const {
    isLoading,
    formData,
    authError,
    user,
    handleChange,
    handleSignIn,
    handlePasswordReset,
  } = useAuth();

  // Check user role when user changes
  useEffect(() => {
    const checkUserRole = async () => {
      if (!user) return;
      
      try {
        setRedirecting(true);
        const { data } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        setUserRole(data?.role || 'learner');
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };
    
    if (user) {
      checkUserRole();
    }
  }, [user]);

  // Redirect if user is already logged in - based on role
  if (user) {
    if (userRole === 'admin' || userRole === 'instructor') {
      return <Navigate to="/admin/dashboard" />;
    } else {
      return <Navigate to="/courses" />;
    }
  }

  // Check if we're coming from a password reset or verification flow
  const isPasswordResetFlow = location.search.includes("type=recovery");
  const isVerificationFlow = location.search.includes("type=signup");

  return (
    <Layout>
      {/* Process email verification if tokens are present in URL */}
      <EmailVerification 
        setVerificationError={setVerificationError}
        setVerificationDialogOpen={setVerificationDialogOpen}
      />
      
      <AuthFormContainer 
        title="Sign in to BoardSight"
        description="Access your dashboard, courses and admin tools">
        <div className="space-y-6">
          {isVerificationFlow ? (
            <div className="bg-green-50 p-4 rounded-md border border-green-100">
              <h2 className="font-semibold text-green-800 mb-2">Email Verification</h2>
              <p className="text-green-700 text-sm">
                Thank you for verifying your email address. You can now sign in to access your account.
              </p>
            </div>
          ) : isPasswordResetFlow ? (
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
              <h2 className="font-semibold text-blue-800 mb-2">Password Reset</h2>
              <p className="text-blue-700 text-sm">
                You've been redirected here to set a new password.
              </p>
            </div>
          ) : null}

          <Alert variant="default" className="bg-techne-purple/5 border-techne-purple/20">
            <AlertDescription className="text-center">
              <p className="mb-2">
                Welcome to BoardSight. Log in to access your account.
              </p>
              <Link to="/blueprint">
                <Button variant="default" className="bg-techne-purple hover:bg-techne-deepPurple mt-2">
                  Learn About Our Program
                </Button>
              </Link>
            </AlertDescription>
          </Alert>

          <SignInForm
            email={formData.email}
            password={formData.password}
            isLoading={isLoading || redirecting}
            authError={authError}
            handleChange={handleChange}
            handleSignIn={handleSignIn}
            handlePasswordReset={handlePasswordReset}
          />
        </div>
      </AuthFormContainer>

      {/* Verification Error Dialog */}
      <VerificationErrorDialog
        isOpen={verificationDialogOpen}
        onClose={() => setVerificationDialogOpen(false)}
        errorMessage={verificationError}
      />
    </Layout>
  );
};

export default AuthPage;
